import { useState, useEffect, type ReactNode } from 'react';
import { CaretDown } from '@phosphor-icons/react';
import styles from './DetailSection.module.css';

/**
 * Tarjeta plegable ("isla") para los paneles de detalle del admin: fondo
 * blanco, borde y sombra suaves, cabecera con chevron giratorio, título y un
 * slot a la derecha para chips/contadores. Se apilan con separación mínima
 * sobre el fondo gris del panel.
 *
 * El estado abierto/cerrado se persiste en localStorage con `storageKey`.
 *
 * ⚠️ Tenía un prop `accent` ('amber' = interno / 'blue' = público) que pintaba
 * un carril de color a la izquierda. Se quitó en el v3 (2026-09-25) porque
 * **no lo usaba ni una sola llamada** y porque el azul tiraba de
 * `--st-nuevo-fg`, hermano del turquesa que entonces rellenaba la cabecera
 * (blanca con línea de tinta desde el v4, paso 43b). La distinción
 * interno/público sigue viva donde de verdad importa, en las burbujas del
 * hilo. Si vuelve a hacer falta aquí, que no sea con un teal.
 */
export interface DetailSectionProps {
  title: ReactNode;
  /** Contenido a la derecha de la cabecera (chip de estado, contador…). */
  right?: ReactNode;
  defaultOpen?: boolean;
  storageKey?: string;
  /**
   * Señal para ABRIR la tarjeta desde fuera: cada vez que este número cambia,
   * la sección se despliega (nunca se pliega).
   *
   * Existe por el botón «Resolver» de la cabecera del ticket, que es un atajo
   * a esta misma tarjeta. Sin la señal, pulsarlo con la tarjeta plegada no
   * enseñaba nada: ni el aviso de respuesta resolutoria ni el botón que
   * confirma el cambio, así que parecía que el botón estaba roto.
   *
   * No es un `open` controlado a propósito: el estado normal lo sigue
   * mandando la persona y se sigue recordando en `localStorage`. Esto solo
   * empuja en una dirección.
   */
  openSignal?: number;
  children: ReactNode;
}

const PREFIX = 'admin:collapse:';

function loadState(storageKey: string | undefined, defaultOpen: boolean): boolean {
  if (!storageKey || typeof window === 'undefined') return defaultOpen;
  try {
    const raw = localStorage.getItem(PREFIX + storageKey);
    if (raw === 'open') return true;
    if (raw === 'closed') return false;
  } catch {
    // ignore (private mode / quota)
  }
  return defaultOpen;
}

function saveState(storageKey: string | undefined, open: boolean): void {
  if (!storageKey || typeof window === 'undefined') return;
  try {
    localStorage.setItem(PREFIX + storageKey, open ? 'open' : 'closed');
  } catch {
    // ignore
  }
}

export function DetailSection({
  title,
  right,
  defaultOpen = true,
  storageKey,
  openSignal,
  children,
}: DetailSectionProps) {
  const [open, setOpen] = useState(() => loadState(storageKey, defaultOpen));

  useEffect(() => {
    saveState(storageKey, open);
  }, [storageKey, open]);

  // `openSignal === undefined` (el caso de todos los demás llamantes) no llega
  // a disparar nada porque el valor nunca cambia.
  useEffect(() => {
    if (openSignal === undefined || openSignal === 0) return;
    setOpen(true);
  }, [openSignal]);

  return (
    <div className={styles.section}>
      <button
        type="button"
        className={`${styles.head} ${open ? styles.headOpen : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span className={`${styles.chev} ${open ? styles.chevOpen : ''}`}>
          <CaretDown weight="bold" size={13} />
        </span>
        <span className={styles.title}>{title}</span>
        {right && <span className={styles.right}>{right}</span>}
      </button>
      {open && <div className={styles.body}>{children}</div>}
    </div>
  );
}
